import type { QueryKey } from '@tanstack/react-query';
import {
  useController,
  type FieldValues,
  type Path,
  type PathValue,
  type UseControllerProps,
  type UseControllerReturn,
} from 'react-hook-form';
import { SelectAsyncSearchable, type Item } from './SelectAsyncSearchable.tsx';

type Props<
  T extends FieldValues,
  N extends Path<T>,
  TQueryData,
  TQueryError extends Error,
  TQueryKey extends QueryKey,
> = {
  renderOptions: ({
    onChange,
    items,
  }: {
    onChange: UseControllerReturn['field']['onChange'];
    items: Item[];
  }) => NonNullable<React.ReactElement>;
  renderValue?: (val: PathValue<T, N>) => string | null;
} & Omit<
  React.ComponentPropsWithoutRef<typeof SelectAsyncSearchable<PathValue<T, N>, TQueryData, TQueryError, TQueryKey>>,
  'children' | 'renderValue' | 'value'
> &
  UseControllerProps<T, N>;

export function SelectAsyncSearchableControlled<
  T extends FieldValues,
  N extends Path<T>,
  TQueryData,
  TQueryError extends Error,
  TQueryKey extends QueryKey,
>({ control, rules, name, renderOptions, ...props }: Props<T, N, TQueryData, TQueryError, TQueryKey>) {
  const {
    field: { ref, onChange, value, onBlur },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <SelectAsyncSearchable
      onBlur={onBlur}
      innerRef={ref}
      defaultValue={value ?? undefined}
      value={value}
      onChange={onChange}
      {...props}
    >
      {(items) => renderOptions({ onChange, items })}
    </SelectAsyncSearchable>
  );
}
